import React, { useEffect, useMemo, useState } from 'react';
import { Container, Typography, Button, Box, Stack, Alert } from '@mui/material';
import {
  hasConsent,
  getConsent,
  acceptConsent,
  declineConsent,
  ConsentState,
  getConsentTimestamp,
  getConsentExpiryDate,
} from '../../utils/cookieConsent';
import { initializeAnalytics, resetAnalytics, track } from '../../services/analytics';

const formatDate = (date) => (date ? new Date(date).toLocaleString() : 'Not recorded');

function CookiePreferences() {
  const [consent, setConsent] = useState(() => getConsent());
  const consentGiven = consent === ConsentState.ACCEPTED;
  const consentTimestamp = useMemo(() => getConsentTimestamp(), [consent]);
  const consentExpiry = useMemo(() => getConsentExpiryDate(), [consent]);

  useEffect(() => {
    setConsent(getConsent());
  }, []);

  const handleAccept = async () => {
    acceptConsent();
    setConsent(ConsentState.ACCEPTED);

    // Initialize analytics and send acknowledgement event
    await initializeAnalytics();
    track('Cookie Preferences Updated', {
      status: 'accepted',
      updatedAt: new Date().toISOString(),
    });
  };

  const handleDecline = () => {
    declineConsent();
    setConsent(ConsentState.DECLINED);

    resetAnalytics();
  };

  return (
    <Container maxWidth="md" sx={{ py: 6 }}>
      <Stack spacing={4}>
        <Box>
          <Typography variant="h3" component="h1" gutterBottom>
            Cookie Preferences
          </Typography>
          <Typography variant="body1" color="text.secondary">
            You can manage your analytics consent at any time. We only activate tracking after you explicitly opt in.
          </Typography>
        </Box>

        <Alert severity={consentGiven ? 'success' : 'info'}>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Current status: {consentGiven ? 'Accepted' : consent === ConsentState.DECLINED ? 'Declined' : 'Unset'}
          </Typography>
          <Typography variant="body2">
            Last updated: {formatDate(consentTimestamp)}
          </Typography>
          <Typography variant="body2">
            Expires: {formatDate(consentExpiry)}
          </Typography>
        </Alert>

        <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
          <Button
            variant="contained"
            color="primary"
            onClick={handleAccept}
            disabled={consentGiven && hasConsent()}
          >
            Accept Analytics Cookies
          </Button>
          <Button
            variant="outlined"
            color="inherit"
            onClick={handleDecline}
            disabled={consent === ConsentState.DECLINED}
          >
            Decline Analytics Cookies
          </Button>
        </Stack>
      </Stack>
    </Container>
  );
}

export default CookiePreferences;
